import React from 'react';
import styled from 'styled-components';
import ReactModal from 'react-modal';
import { withHandlers, withStateHandlers, compose } from 'recompose';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { Button, ButtonBorder, ButtonWarning } from '../../../ui/atoms';
import { View as ButtonView } from 'planoplan-ui-kit/core/button';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { IClose } from 'planoplan-ui-kit/core/icons';

const customStyles = {
  content: {
    top: '0',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    border: 'none',
    background: 'transparent',
    padding: '0',
    overflow: 'initial',
  },
  overlay: {
    zIndex: '10000',
    backgroundColor: 'none',
  },
};

const closeTimeout = 300;
const rootElement = document.getElementById('root');

ReactModal.setAppElement('#modal-root');

export const Modal = ({ children, closeModal, ...props }) => (
  <ReactModal closeTimeoutMS={closeTimeout} style={customStyles} onRequestClose={closeModal} {...props}>
    <Wrapper>
      {children}
      <CloseButton onClick={closeModal}>
        <IClose fill={Colors.white} />
      </CloseButton>
    </Wrapper>
  </ReactModal>
);

export const withModals = (modals) =>
  compose(
    withStateHandlers(
      () => {
        return modals.reduce(
          (acc, modal) => {
            acc.modals[modal] = {
              isOpen: false,
              data: null,
            };
            return acc;
          },
          { modals: {} }
        );
      },
      {
        setModal: () => (value) => {
          return {
            modals: value,
          };
        },
      }
    ),
    withHandlers({
      openModal: ({ modals, setModal }) => (modal, data) => {
        disableBodyScroll(rootElement, { reserveScrollBarGap: true });
        const closedModals = { ...modals };

        for (let i in closedModals) {
          closedModals[i] = {
            ...closedModals[i],
            isOpen: false,
          };
        }

        setModal({
          ...closedModals,
          [modal]: {
            isOpen: true,
            data,
          },
        });
      },
      closeModals: ({ modals, setModal }) => () => {
        const closedModals = { ...modals };

        for (let i in closedModals) {
          closedModals[i] = {
            ...closedModals[i],
            isOpen: false,
          };
        }

        setModal({
          ...closedModals,
        });

        setTimeout(() => {
          enableBodyScroll(rootElement);
        }, closeTimeout);
      },
    })
  );

const CloseButton = styled.div`
  background: none;
  border: none;
  cursor: pointer;
  position: absolute;
  right: -60px;
  top: 2px;
`;

const Wrapper = styled.div`
  position: relative;
  margin-top: 24px;
  margin-bottom: 12px;
`;

export const Container = styled.div`
  background-color: ${Colors.white};
  display: flex;
  flex-direction: column;
  width: 420px;
  min-height: 360px;
  padding: 38px;
  border-radius: 10px;
  box-sizing: border-box;
`;

export const Heading = styled.h3`
  font-family: 'Montserrat', serif;
  font-size: 24px;
  line-height: 30px;
  font-weight: 700;
  margin-top: 0;
  margin-bottom: 14px;
`;

export const Text = styled.div`
  font-size: 20px;
  font-weight: 300;
  margin-bottom: 14px;
  line-height: 1.2;

  b {
    font-weight: 400;
  }
`;

export const Form = styled.form`
  margin-top: auto;
`;

export const ButtonsArea = styled.div`
  margin-top: 40px;
  display: flex;
  justify-content: space-between;

  ${ButtonView} {
    width: 160px;
  }
`;

export const SubmitButton = styled(Button)`
  height: 40px;
`;

export const CancelButton = styled(ButtonBorder)`
  height: 40px;
`;

export const WarningButton = styled(ButtonWarning)`
  height: 40px;
`;

export const Input = styled.input`
  height: 40px;
  width: 100%;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid ${Colors.whisper};
  font-size: 16px;
  font-weight: 300;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  padding-left: 10px;
  padding-right: 10px;
  background-color: ${Colors.mint};
`;

export const Select = styled.select`
  height: 36px;
  width: 100%;
  font-size: 16px;
  padding: 0 12px;
  margin-top: 14px;
`;

export const SelectWrapper = styled.div`
  margin-top: 14px;
`;

export const Option = styled.option``;
